import { tt } from './i18n'
import { formatCents, parseMoneyToCents } from './money'

/**
 * What is owed between an external courier and the restaurant - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 D,
 * owner O139.
 *
 * The owner: *"šofér keď príde pre obj hneď platí tu obj a zákazník to vyplatí jemu"* - he pays for a cash order at
 * pickup and keeps his reward, and for a paid one the restaurant pays him the reward. The server keeps the balance
 * (`CourierService.balance`); this reads it. **One reader for two screens**: the courier's own *Zárobok* and the
 * board's settling dialog at *Vyraziť* both draw it, and two copies of a money reader is how two screens come to
 * disagree about one figure.
 *
 * Money arrives as decimal euros and is held here in whole cents. **Positive: the courier owes the restaurant.
 * Negative: the restaurant owes him** - the server's own sign, so a settlement of exactly `owes` clears it.
 */

export interface CourierBalanceOrder {
  id: number
  orderNumber: string
  status: string
  paymentMethod: string
  totalCents: number
  rewardCents: number
  /** What he collects at the door for it: the total for a cash order, nought for one already paid. */
  cashCents: number
}

export interface CourierSettlement {
  id: number
  /** Positive: he paid the restaurant. Negative: the restaurant paid him. */
  amountCents: number
  note: string | null
  createdAt: string | null
}

export interface CourierBalance {
  /** Positive: he owes the restaurant. Negative: the restaurant owes him. */
  owesCents: number
  today: CourierBalanceOrder[]
  todayRewardsCents: number
  todaySettlements: CourierSettlement[]
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function decimal(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return null
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (one): one is Record<string, unknown> =>
          typeof one === 'object' && one !== null && typeof (one as Record<string, unknown>).id === 'number',
      )
    : []
}

/** The balance, or null when the answer carries no figure - which a screen reports rather than draws as a nought. */
export function courierBalanceOf(payload: unknown): CourierBalance | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null
  const row = payload as Record<string, unknown>
  const owes = decimal(row.owes)
  if (owes === null) return null
  return {
    owesCents: parseMoneyToCents(owes),
    today: rows(row.today).map((one) => ({
      id: one.id as number,
      orderNumber: text(one.orderNumber) ?? String(one.id),
      status: text(one.status) ?? '',
      paymentMethod: text(one.paymentMethod) ?? '',
      totalCents: parseMoneyToCents(decimal(one.total) ?? 0),
      rewardCents: parseMoneyToCents(decimal(one.reward) ?? 0),
      cashCents: parseMoneyToCents(decimal(one.cash) ?? 0),
    })),
    todayRewardsCents: parseMoneyToCents(decimal(row.todayRewards) ?? 0),
    todaySettlements: rows(row.todaySettlements).map((one) => ({
      id: one.id as number,
      amountCents: parseMoneyToCents(decimal(one.amount) ?? 0),
      note: text(one.note),
      createdAt: text(one.createdAt),
    })),
  }
}

/** The staff's read of one courier's balance, and where a payment between them is recorded. */
export function courierBalancePath(employeeId: number): string {
  return `/api/v1/employees/${String(employeeId)}/courier-balance`
}

export function courierSettlementsPath(employeeId: number): string {
  return `/api/v1/employees/${String(employeeId)}/courier-settlements`
}

/**
 * The one amount, from the restaurant's side - the settling dialog at *Vyraziť*: *Šofér zaplatí 11,00 €* when he
 * owes, *Vyplaťte šoférovi 4,50 €* when the restaurant does, *Nič sa nevyrovnáva* when they are square.
 */
export function restaurantSettleNote(owesCents: number): string {
  if (owesCents > 0) return `${tt('Šofér zaplatí')} ${formatCents(owesCents)}`
  if (owesCents < 0) return `${tt('Vyplaťte šoférovi')} ${formatCents(-owesCents)}`
  return tt('Nič sa nevyrovnáva')
}
